import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  DollarSign,
  AlertTriangle,
  Clock,
  FileText,
  ExternalLink,
  Upload,
  Truck,
  Ship,
  Plane,
  Train,
  Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageHeader, MetricCard, KPIStrip } from '@/components/common';
import {
  rateAnalyticsApi,
  RateAnalyticsSummary,
  ExpiringRate,
  RateActivityPoint,
  TopCarrier,
  TransportMode,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { formatCurrency as formatMoney } from '@/lib/utils';

// Chart colors - using CSS variables
const MODE_COLORS: Record<TransportMode, string> = {
  TruckFTL: 'var(--color-primary-500)',
  TruckLTL: 'var(--color-primary-400)',
  Ocean: 'var(--color-success-500)',
  Air: 'var(--color-purple-500)',
  Rail: 'var(--color-warning-500)',
  Intermodal: 'var(--color-primary-600)',
};

const PIE_COLORS = [
  'var(--color-primary-500)',
  'var(--color-success-500)',
  'var(--color-purple-500)',
  'var(--color-warning-500)',
  'var(--color-primary-600)',
  'var(--color-pink-500)',
  'var(--color-teal-500)',
];
const BAR_COLORS = [
  'var(--color-primary-500)',
  'var(--color-success-500)',
  'var(--color-purple-500)',
  'var(--color-warning-500)',
  'var(--color-danger-500)',
  'var(--color-primary-600)',
  'var(--color-teal-500)',
];

const MODE_ICONS: Record<TransportMode, React.ReactNode> = {
  TruckFTL: <Truck className="w-4 h-4" />,
  TruckLTL: <Truck className="w-4 h-4" />,
  Ocean: <Ship className="w-4 h-4" />,
  Air: <Plane className="w-4 h-4" />,
  Rail: <Train className="w-4 h-4" />,
  Intermodal: <Package className="w-4 h-4" />,
};

export default function RateDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<RateAnalyticsSummary | null>(null);
  const [expiringRates, setExpiringRates] = useState<ExpiringRate[]>([]);
  const [activity, setActivity] = useState<RateActivityPoint[]>([]);
  const [topCarriers, setTopCarriers] = useState<TopCarrier[]>([]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      // Helper to safely call API and return null on error
      const safeCall = async <T,>(fn: () => Promise<T>): Promise<T | null> => {
        try {
          return await fn();
        } catch (error) {
          // Silently handle 404 errors (backend not implemented yet)
          const errorMessage = getErrorMessage(error);
          if (
            !errorMessage.includes('404') &&
            !errorMessage.includes('Not Found') &&
            !errorMessage.includes('Cannot GET')
          ) {
            console.error('API error:', errorMessage);
          }
          return null;
        }
      };

      const [summaryData, expiringData, activityData, carriersData] = await Promise.all([
        safeCall(() => rateAnalyticsApi.getSummary()),
        safeCall(() => rateAnalyticsApi.getExpiring(30)),
        safeCall(() => rateAnalyticsApi.getActivity(30)),
        safeCall(() => rateAnalyticsApi.getTopCarriers(5)),
      ]);

      if (summaryData) setSummary(summaryData);
      if (expiringData) setExpiringRates(expiringData);
      if (activityData) setActivity(activityData);
      if (carriersData) setTopCarriers(carriersData);

      setLoading(false);
    };

    loadData();
  }, []);

  /**
   * Delegates to the shared formatter so the currency comes from the record
   * rather than from a hardcoded 'USD' in this file. A page-local formatter is
   * how an order billed in dirhams ends up rendered with a dollar sign.
   */
  const formatCurrency = (amount: number, currency = 'USD') => formatMoney(amount, currency);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <PageHeader
          title="Rate Dashboard"
          description="Contracted freight rates by lane and carrier, with variance against the rates last agreed."
        />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Rate Dashboard"
          description="Overview of rate management and analytics"
          actions={
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate('/tms/rate-import')}>
                <Upload className="w-4 h-4 mr-2" />
                Import Rates
              </Button>
              <Button onClick={() => navigate('/tms/rates')}>
                <FileText className="w-4 h-4 mr-2" />
                Manage Rates
              </Button>
            </div>
          }
        />
      </motion.div>

      {/* KPIs */}
      <div className="mt-6">
        <KPIStrip columns={4} className="lg:grid-cols-5 xl:grid-cols-5">
          <MetricCard
            label="Active Rates"
            value={formatNumber(summary?.activeRates || 0)}
            sentiment="success"
            icon={<FileText className="w-5 h-5" />}
            subtitle="this month"
          />
          <MetricCard
            label="Total Rates"
            value={formatNumber(summary?.totalRates || 0)}
            sentiment="info"
            icon={<DollarSign className="w-5 h-5" />}
          />
          <MetricCard
            label="Contract Rates"
            value={formatNumber(summary?.contractRates || 0)}
            sentiment="info"
            icon={<FileText className="w-5 h-5" />}
          />
          <MetricCard
            label="Expiring Soon"
            value={formatNumber(summary?.expiringRates || 0)}
            sentiment={(summary?.expiringRates || 0) > 0 ? 'warning' : 'success'}
            icon={<AlertTriangle className="w-5 h-5" />}
            subtitle="30 days"
          />
        </KPIStrip>
      </div>

      {/* Charts Row */}
      <motion.div variants={fadeUp} className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Rates by Mode Pie Chart */}
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground">Rates by Mode</h3>
          </CardHeader>
          <CardContent>
            {summary?.byMode && summary.byMode.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={summary.byMode}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="count"
                      nameKey="mode"
                      label={({ mode, count }) => `${mode}: ${count}`}
                      labelLine={false}
                    >
                      {summary.byMode.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={MODE_COLORS[entry.mode] || PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number, name: string) => [formatNumber(value), name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rates by Category Bar Chart */}
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground">Rates by Category</h3>
          </CardHeader>
          <CardContent>
            {summary?.byCategory && summary.byCategory.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summary.byCategory} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" />
                    <YAxis dataKey="category" type="category" width={80} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value: number) => [formatNumber(value), 'Rates']} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {summary.byCategory.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={BAR_COLORS[index % BAR_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Activity Line Chart */}
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground">Rate Activity (30 Days)</h3>
          </CardHeader>
          <CardContent>
            {activity.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={activity}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(v) =>
                        new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      }
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      labelFormatter={(label) =>
                        new Date(label).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      }
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="created"
                      name="Created"
                      stroke="#10B981"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="expired"
                      name="Expired"
                      stroke="#EF4444"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="updated"
                      name="Updated"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No activity data
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Bottom Row */}
      <motion.div variants={fadeUp} className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Expiring Rates */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">
              <Clock className="w-4 h-4 text-status-warning" />
              Expiring Rates
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/tms/rate-analytics?tab=expiring')}
            >
              View All
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </CardHeader>
          <CardContent>
            {expiringRates.length > 0 ? (
              <div className="space-y-3">
                {expiringRates.slice(0, 5).map((rate) => (
                  <div
                    key={rate.id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div className="space-y-1">
                      <div className="font-medium text-sm">{rate.sku}</div>
                      <div className="text-xs text-muted-foreground">
                        {rate.carrier.name} • {rate.originPort} → {rate.destinationPort}
                      </div>
                    </div>
                    <div className="text-right space-y-1">
                      <div className="font-semibold">
                        {formatCurrency(rate.baseRate, rate.currency)}
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          rate.daysUntilExpiry <= 7
                            ? 'bg-danger-50 text-danger-600'
                            : rate.daysUntilExpiry <= 14
                              ? 'bg-warning-50 text-warning-600'
                              : 'bg-primary-50 text-primary-500'
                        }
                      >
                        {rate.daysUntilExpiry}d left
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No rates expiring in the next 30 days</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Carriers */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">
              <Truck className="w-4 h-4 text-status-info" />
              Top Carriers by Rates
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/tms/rate-analytics?tab=carrier')}
            >
              View All
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </CardHeader>
          <CardContent>
            {topCarriers.length > 0 ? (
              <div className="space-y-3">
                {topCarriers.map((carrier, idx) => (
                  <div
                    key={carrier.carrierId}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                        {idx + 1}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{carrier.carrierName}</div>
                        <div className="text-xs text-muted-foreground">{carrier.carrierCode}</div>
                      </div>
                    </div>
                    <div className="text-right space-y-1">
                      <div className="font-semibold">{formatNumber(carrier.rateCount)} rates</div>
                      <div className="text-xs text-muted-foreground">
                        Avg: {formatCurrency(carrier.avgRate)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Truck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No carrier data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Quick Stats */}
      <motion.div variants={fadeUp} className="mt-6">
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground">Mode Breakdown</h3>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {(summary?.byMode || []).map((mode) => (
                <div key={mode.mode} className="p-4 bg-muted/30 rounded-lg text-center">
                  <div className="flex items-center justify-center mb-2">
                    <div
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${MODE_COLORS[mode.mode]}20` }}
                    >
                      {MODE_ICONS[mode.mode]}
                    </div>
                  </div>
                  <div className="text-2xl font-bold">{formatNumber(mode.count)}</div>
                  <div className="text-sm text-muted-foreground">{mode.mode}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Avg: {formatCurrency(mode.avgRate)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.main>
  );
}
